/**
 * BodyweightLog
 *
 * Card on the Dashboard for logging and visualising bodyweight over time.
 * - Log today's weight (one tap, numeric input)
 * - Sparkline trend for the last 12 entries
 * - Shows delta vs 7 days ago
 */

import { useState, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import type { BodyweightEntry } from '@/types';

// ── Tiny sparkline ────────────────────────────────────────────

function Sparkline({ data }: { data: number[] }) {
  if (data.length < 2) return null;
  const W = 200, H = 48, PAD = 4;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = PAD + (i / (data.length - 1)) * (W - PAD * 2);
    const y = H - PAD - ((v - min) / range) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = `M ${pts.join(' L ')}`;
  const fillPath = `${path} L ${(W - PAD).toFixed(1)},${H - PAD} L ${PAD},${H - PAD} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 48 }}>
      <defs>
        <linearGradient id="bw-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#7c3aed" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill="url(#bw-grad)" />
      <path d={path} stroke="#7c3aed" strokeWidth="2" fill="none"
            strokeLinecap="round" strokeLinejoin="round" />
      {/* Last point dot */}
      {(() => {
        const last = pts[pts.length - 1].split(',');
        return <circle cx={last[0]} cy={last[1]} r="3" fill="#7c3aed" />;
      })()}
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────

export default function BodyweightLog() {
  const [inputVal, setInputVal] = useState('');
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const entries = useLiveQuery(
    () => db.bodyweightLog.orderBy('loggedAt').reverse().limit(30).toArray(),
    [],
  ) ?? [];

  // Today's entry (if any)
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayEntry = entries.find((e) => e.loggedAt.slice(0, 10) === todayKey);

  // Last 12 entries for sparkline (chronological)
  const sparkData = [...entries].reverse().slice(-12).map((e) => e.weightLbs);

  // Delta vs 7 days ago
  const sevenDaysAgo = new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10);
  const weekAgoEntry = entries.find((e) => e.loggedAt.slice(0, 10) <= sevenDaysAgo);
  const delta = todayEntry && weekAgoEntry
    ? (todayEntry.weightLbs - weekAgoEntry.weightLbs)
    : null;

  const handleLog = async () => {
    const val = parseFloat(inputVal);
    if (!val || isNaN(val) || val < 50 || val > 999) return;

    const now = new Date().toISOString();
    // Upsert — replace today's entry if exists
    const id = todayEntry?.id ?? 'bw_' + Date.now();
    await db.bodyweightLog.put({ id, weightLbs: val, loggedAt: now } as BodyweightEntry);
    setSaved(true);
    setEditing(false);
    setInputVal('');
    setTimeout(() => setSaved(false), 2000);
  };

  const latestWeight = todayEntry?.weightLbs ?? entries[0]?.weightLbs;
  const latestDate   = entries[0] ? new Date(entries[0].loggedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;

  return (
    <div className="card space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-widest text-ink-faint">
          Body Weight
        </p>
        {!editing && (
          <button
            className="text-xs text-accent font-medium px-2 py-1 rounded-lg
                       bg-accent/10 active:scale-95 transition-transform"
            onClick={() => {
              setEditing(true);
              setInputVal(todayEntry ? String(todayEntry.weightLbs) : '');
              setTimeout(() => inputRef.current?.focus(), 60);
            }}
          >
            {todayEntry ? 'Edit' : '+ Log'}
          </button>
        )}
      </div>

      {/* Current weight display */}
      {latestWeight && !editing && (
        <div className="flex items-end gap-3">
          <div>
            <span className="text-3xl font-bold text-ink font-mono">
              {latestWeight.toFixed(1)}
            </span>
            <span className="text-ink-muted text-sm ml-1">lbs</span>
          </div>
          {delta !== null && (
            <span className={`text-sm font-semibold mb-0.5 ${
              delta < 0 ? 'text-success' : delta > 0 ? 'text-danger' : 'text-ink-muted'
            }`}>
              {delta > 0 ? '+' : ''}{delta.toFixed(1)} vs 7d
            </span>
          )}
          {latestDate && (
            <span className="text-xs text-ink-faint mb-0.5 ml-auto">{latestDate}</span>
          )}
        </div>
      )}

      {/* Sparkline */}
      {sparkData.length >= 2 && !editing && (
        <Sparkline data={sparkData} />
      )}

      {/* Empty state */}
      {entries.length === 0 && !editing && (
        <p className="text-xs text-ink-muted">Log your weight daily to track trends.</p>
      )}

      {/* Saved flash */}
      {saved && (
        <p className="text-xs text-success font-medium animate-fade-in">✓ Weight logged</p>
      )}

      {/* Input form */}
      {editing && (
        <div className="flex gap-2 animate-fade-in">
          <input
            ref={inputRef}
            type="number"
            inputMode="decimal"
            placeholder="e.g. 175.5"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLog()}
            className="flex-1 bg-surface-300 rounded-xl px-3 py-2.5 text-ink text-sm
                       focus:outline-none focus:ring-2 focus:ring-accent font-mono"
          />
          <button className="btn-primary px-4 py-2 text-sm" onClick={handleLog}>Save</button>
          <button className="btn-secondary px-3 py-2 text-sm" onClick={() => setEditing(false)}>✕</button>
        </div>
      )}
    </div>
  );
}
